import React from 'react';
import './AboutStatistics.scss';
import Reveal from '../Reveal/Reveal';
// Import PrimeReact Chart component for displaying data visualizations
import { Chart } from 'primereact/chart';
// Import brothers data from the main data file
import { brothers } from '../../BrothersObjects';

// Chart palette anchors — mirror $ink-soft (#5b554c) from variables.scss.
// Chart.js options are plain JS, so this is the single source for the value
// instead of a second hardcoded hex living inside `lightOptions` below.
const CHART_LEGEND_TEXT_COLOR = '#5b554c'; // mirrors $ink-soft

// Generates `count` on-brand hex swatches, interpolating from a warm
// paper-adjacent light tone down to a deep red-black. Keeps every category
// chart in the site's canonical red family (DESIGN.md §2) instead of the
// unrelated dashboard rose/mauve palette this replaced, and — unlike a fixed
// hex array — never runs short of colors if a category list grows.
const RAMP_LIGHT = { r: 0xf1, g: 0xdd, b: 0xd2 };
const RAMP_DARK = { r: 0x3a, g: 0x09, b: 0x0b };

function redRamp(count) {
    if (count <= 1) return ['#9a1b1d'];
    return Array.from({ length: count }, (_, i) => {
        const t = i / (count - 1);
        const channel = (from, to) => Math.round(from + (to - from) * t);
        const r = channel(RAMP_LIGHT.r, RAMP_DARK.r);
        const g = channel(RAMP_LIGHT.g, RAMP_DARK.g);
        const b = channel(RAMP_LIGHT.b, RAMP_DARK.b);
        return `#${[r, g, b].map((c) => c.toString(16).padStart(2, '0')).join('')}`;
    });
}

// Calculate statistics about brothers by year
// Filters the brothers array to count members in each year
const brothersData = {
    totalBrothers: brothers.length,
    // Count brothers by year (1st through 5th year)
    fifthYearBrothers: brothers.filter(brother => brother.year === '5').length,
    fourthYearBrothers: brothers.filter(brother => brother.year === '4').length,
    thirdYearBrothers: brothers.filter(brother => brother.year === '3').length,
    secondYearBrothers: brothers.filter(brother => brother.year === '2').length,
    firstYearBrothers: brothers.filter(brother => brother.year === '1').length,
};

// Major distribution, derived directly from the roster instead of a
// hand-maintained major whitelist. The previous whitelist silently dropped
// any major not already on it — it was missing 'GE' and 'BRAE', so two real
// members vanished from the chart and its total didn't match the page's own
// "Total Members" count. Deriving from the data can't drift out of sync
// again, and a newly-added major shows up automatically.
//
// IME and IE are combined into one 'IE/IME' slice per chapter request — both
// are industrial-engineering variants and read as one category here.
const MAJOR_MERGES = { IME: 'IE/IME', IE: 'IE/IME' };

const majorCounts = new Map();
brothers.forEach((brother) => {
    const rawMajor = brother.major && brother.major[1];
    if (!rawMajor) return;
    const major = MAJOR_MERGES[rawMajor] || rawMajor;
    majorCounts.set(major, (majorCounts.get(major) || 0) + 1);
});

// Largest slice first reads better in a pie legend than alphabetical order.
const majorEntries = [...majorCounts.entries()].sort((a, b) => b[1] - a[1]);
const label = majorEntries.map(([major]) => major);
const majorlengths = majorEntries.map(([, count]) => count);

// Component that displays chapter statistics using doughnut charts
// Shows breakdown by year and by major
function AboutStatistics() {

    // Chart configuration for year distribution
    // Displays how many brothers are in each year (1st through 5th)
    const yearColors = redRamp(5);
    const yearChart = {
        labels: ['1st', '2nd', '3rd', '4th', '5th'],
        datasets: [
            {
                data: [brothersData.firstYearBrothers,
                        brothersData.secondYearBrothers, 
                        brothersData.thirdYearBrothers, 
                        brothersData.fourthYearBrothers,
                        brothersData.fifthYearBrothers
                    ],
                backgroundColor: yearColors,
                hoverBackgroundColor: yearColors
            }]
    };

    // Chart configuration for major distribution
    // Displays how many brothers are in each engineering major
    const majorColors = redRamp(label.length);
    const majorChart = {
        labels: label,
        datasets: [
            {   // Data array containing count of brothers per major
                data: majorlengths,
                backgroundColor: majorColors,
                hoverBackgroundColor: majorColors
            }]
    };

    // Chart options for styling (colors, fonts, etc.)
    // Chart.js 2.9.x shape: legend lives at the top level, not under `plugins`.
    const lightOptions = {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
            position: 'bottom',
            labels: {
                fontColor: CHART_LEGEND_TEXT_COLOR,
                fontFamily: "'LibreFranklin', 'Helvetica Neue', Arial, sans-serif",
                fontSize: 13,
                boxWidth: 14,
                padding: 14,
            }
        }
    };

    return (
        <section className="chapter-ledger-stats" aria-labelledby="chapter-ledger-stats-heading">
            {/* Main title for the statistics section */}
            <div className="chapter-ledger-stats-heading">
                <Reveal as="h2" id="chapter-ledger-stats-heading" variant="mask">Chapter Statistics</Reveal>
                {/* Display total number of members */}
                <Reveal as="p" variant="rise" index={1} className="chapter-ledger-stats-total">Total Members: {brothersData.totalBrothers}</Reveal>
            </div>
            {/* Container for all chart visualizations */}
            <div className="chapter-ledger-stats-donuts">
                {/* Year distribution chart */}
                <Reveal
                    as="div"
                    variant="rise"
                    index={2}
                    className="Donut"
                    role="group"
                    aria-labelledby="chapter-ledger-stats-year-title"
                >
                    <h3 className="ChartTitle" id="chapter-ledger-stats-year-title">Year</h3>
                    <div aria-hidden="true">
                        <Chart type="doughnut" data={yearChart} options={lightOptions} />
                    </div>
                    <table className="visually-hidden">
                        <caption>Brothers by year</caption>
                        <thead>
                            <tr>
                                <th scope="col">Year</th>
                                <th scope="col">Members</th>
                            </tr>
                        </thead>
                        <tbody>
                            {yearChart.labels.map((yearLabel, i) => (
                                <tr key={yearLabel}>
                                    <td>{yearLabel}</td>
                                    <td>{yearChart.datasets[0].data[i]}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Reveal>
                {/* Major distribution chart */}
                <Reveal
                    as="div"
                    variant="rise"
                    index={3}
                    className="Donut"
                    role="group"
                    aria-labelledby="chapter-ledger-stats-major-title"
                >
                    <h3 className="ChartTitle" id="chapter-ledger-stats-major-title">Major</h3>
                    <div aria-hidden="true">
                        <Chart type="doughnut" data={majorChart} options={lightOptions} />
                    </div>
                    <table className="visually-hidden">
                        <caption>Brothers by major</caption>
                        <thead>
                            <tr>
                                <th scope="col">Major</th>
                                <th scope="col">Members</th>
                            </tr>
                        </thead>
                        <tbody>
                            {majorChart.labels.map((majorLabel, i) => (
                                <tr key={majorLabel}>
                                    <td>{majorLabel}</td>
                                    <td>{majorChart.datasets[0].data[i]}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Reveal>
            </div>
        </section>
    );
}

export default AboutStatistics;
